import { Patient } from "@/types/Patient"
import { FC, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { useApp } from "../../AppProvider"
import { LabReport } from "../../types/LabReport"
import { toTitleCase } from "../../utils/str"
import { PatientSelectionModal } from "./PatientSelectionModal"

interface ResultsOverviewProps {
  results: LabReport
  patient?: Patient
  onPatientSelect?: (patient: Patient) => void
}

export const ResultsOverview: FC<ResultsOverviewProps> = ({ results, patient, onPatientSelect }) => {
  const { organizationId } = useParams()
  const { isAnonymous } = useApp()
  const [isPatientSelectionOpen, setIsPatientSelectionOpen] = useState(false)

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900">Overview</h3>
      <div className="mt-2">
        <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-gray-500">Patient</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {patient ? (
                isAnonymous ? (
                  // Show patient name without link for anonymous users
                  <span className="text-sm text-gray-900">{patient.name}</span>
                ) : (
                  <Link
                    to={`/organizations/${organizationId}/patients/${patient._id}`}
                    className="text-primary-600 hover:text-primary-900 text-sm font-medium">
                    {patient.name}
                  </Link>
                )
              ) : isAnonymous ? (
                // Don't show patient selection for anonymous users
                <span className="text-sm text-gray-500">No patient assigned</span>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsPatientSelectionOpen(true)}
                    className="text-primary-600 hover:text-primary-900">
                    Select patient
                  </button>
                </div>
              )}
            </dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-gray-500">Client ID</dt>
            <dd className="mt-1 text-sm text-gray-900">{results.clientId}</dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-gray-500">Status</dt>
            <dd className="mt-1 text-sm text-gray-900">{results.status}</dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-gray-500">Doctor</dt>
            <dd className="mt-1 text-sm text-gray-900">{results.doctorName}</dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-gray-500">Total Test Count</dt>
            <dd className="mt-1 text-sm text-gray-900">{results.subcategories.length ?? 1}</dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-gray-500">Accession ID</dt>
            <dd className="mt-1 text-sm text-gray-900">{results.accessionId}</dd>
          </div>
          <div className="sm:col-span-1">
            <dt className="text-sm font-medium text-gray-500">Source</dt>
            <dd className="mt-1 text-sm text-gray-900">{toTitleCase(results.source)}</dd>
          </div>
        </dl>
      </div>

      {!isAnonymous && (
        <PatientSelectionModal
          open={isPatientSelectionOpen}
          setOpen={setIsPatientSelectionOpen}
          labReport={results}
          organizationId={organizationId}
        />
      )}
    </div>
  )
}
